import { useForm } from 'react-hook-form'
import { useNavigate, useSearchParams } from 'react-router-dom'
import axios from 'axios'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { authService } from '@/services/auth_service'
import { Button } from '@/components/ui/button'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { toast } from 'sonner'

// 密碼複雜度驗證
const passwordSchema = z
  .string()
  .min(6, { message: '密碼長度至少需要 6 個字元' })
  .refine((val) => /[A-Z]/.test(val), { message: '密碼需包含至少一個大寫字母' })
  .refine((val) => /[a-z]/.test(val), { message: '密碼需包含至少一個小寫字母' })
  .refine((val) => /[0-9]/.test(val), { message: '密碼需包含至少一個數字' })
  .refine((val) => /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(val), {
    message: '密碼需包含至少一個特殊符號',
  })

const formSchema = z
  .object({
    account: z.string().min(1, { message: '請輸入帳號' }),
    temp_password: z.string().min(1, { message: '請輸入暫時密碼' }),
    new_password: passwordSchema,
    new_password_confirm: z.string().min(1, { message: '請確認新密碼' }),
  })
  .refine((data) => data.new_password === data.new_password_confirm, {
    message: '兩次輸入的密碼不一致',
    path: ['new_password_confirm'],
  })

type FormValues = z.infer<typeof formSchema>

export default function Signup() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  // 可以從 URL 參數取得帳號（如果從 email/簡訊連結點入）
  const accountFromUrl = searchParams.get('account') || ''

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      account: accountFromUrl,
      temp_password: '',
      new_password: '',
      new_password_confirm: '',
    },
  })

  const onSubmit = async (data: FormValues) => {
    try {
      const response = await authService.activateAccount({
        account: data.account,
        temp_password: data.temp_password,
        new_password: data.new_password,
        new_password_confirm: data.new_password_confirm,
      })

      if (response.success) {
        toast.success('帳號啟用成功，請使用新密碼登入')
        navigate('/login')
      } else {
        toast.error(response.error?.message || '啟用失敗，請確認暫時密碼是否正確')
      }
    } catch (error) {
      const message = axios.isAxiosError<{ error?: { message?: string } }>(error)
        ? error.response?.data?.error?.message
        : undefined
      toast.error(message || '發生錯誤，請稍後再試')
    }
  }

  return (
    <div className="relative min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 overflow-hidden">
      {/* 裝飾性背景元素 */}
      <div className="absolute top-0 left-0 w-full h-full">
        <div className="absolute top-0 right-0 w-1/3 h-1/3 bg-gradient-to-br from-blue-100/30 to-cyan-100/30 rounded-full filter blur-3xl transform translate-x-1/2 -translate-y-1/2" />
        <div className="absolute bottom-0 left-0 w-1/2 h-1/2 bg-gradient-to-tr from-teal-100/30 to-blue-100/30 rounded-full filter blur-3xl transform -translate-x-1/3 translate-y-1/3" />
      </div>

      <div className="relative flex flex-col md:flex-row items-center justify-center min-h-screen p-4">
        <div className="md:flex-1 max-w-md text-center px-8 md:px-8 px-4">
          <div className="transform hover:scale-105 transition-transform duration-300 mb-2 md:mb-6">
            <img src="/images/xlogo.png" alt="logo" className="mx-auto drop-shadow-xl h-auto" />
          </div>
        </div>

        <div className="md:flex-1 max-w-md px-4 md:px-8 w-full">
          <div className="backdrop-blur-sm bg-white/80 p-8 rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.12)] border border-white/20">
            <h2 className="text-2xl font-bold mb-2 bg-gradient-to-r from-[#1698B6] to-[#0F7A8D] bg-clip-text text-transparent">
              首次登入設定
            </h2>
            <p className="text-gray-500 text-sm mb-6">
              請輸入您收到的暫時密碼，並設定新密碼
            </p>
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
                <FormField
                  control={form.control}
                  name="account"
                  render={({ field }) => (
                    <FormItem className="space-y-2">
                      <FormLabel className="text-gray-700">帳號</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="請輸入帳號"
                          className="h-11 bg-white/50 border-gray-200 focus:border-[#1698B6] focus:ring-[#1698B6]/20 transition-all duration-300"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage className="text-red-500" />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="temp_password"
                  render={({ field }) => (
                    <FormItem className="space-y-2">
                      <FormLabel className="text-gray-700">暫時密碼</FormLabel>
                      <FormControl>
                        <Input
                          type="password"
                          placeholder="請輸入收到的暫時密碼"
                          className="h-11 bg-white/50 border-gray-200 focus:border-[#1698B6] focus:ring-[#1698B6]/20 transition-all duration-300"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage className="text-red-500" />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="new_password"
                  render={({ field }) => (
                    <FormItem className="space-y-2">
                      <FormLabel className="text-gray-700">新密碼</FormLabel>
                      <FormControl>
                        <Input
                          type="password"
                          placeholder="請輸入新密碼"
                          className="h-11 bg-white/50 border-gray-200 focus:border-[#1698B6] focus:ring-[#1698B6]/20 transition-all duration-300"
                          {...field}
                        />
                      </FormControl>
                      <p className="text-xs text-gray-500">
                        密碼需至少 6 字元，包含大寫、小寫、數字及特殊符號
                      </p>
                      <FormMessage className="text-red-500" />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="new_password_confirm"
                  render={({ field }) => (
                    <FormItem className="space-y-2">
                      <FormLabel className="text-gray-700">確認新密碼</FormLabel>
                      <FormControl>
                        <Input
                          type="password"
                          placeholder="請再次輸入新密碼"
                          className="h-11 bg-white/50 border-gray-200 focus:border-[#1698B6] focus:ring-[#1698B6]/20 transition-all duration-300"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage className="text-red-500" />
                    </FormItem>
                  )}
                />
                <Button
                  type="submit"
                  className="w-full h-11 bg-gradient-to-r from-[#1698B6] to-[#0F7A8D] hover:from-[#1698B6] hover:to-[#0F7A8D] hover:opacity-90 text-white font-medium rounded-lg transition-all duration-300 transform hover:scale-[1.02] hover:shadow-lg"
                >
                  啟用帳號
                </Button>
                <div className="text-center">
                  <a
                    href="/login"
                    className="text-sm text-[#1698B6] hover:underline"
                  >
                    已有密碼？返回登入
                  </a>
                </div>
              </form>
            </Form>
          </div>
        </div>
      </div>
    </div>
  )
}
